// Shared placement decision for enum-value completion, used by all five
// dialect resolvers so they behave identically.
//
// Finding 2a: when the cursor is inside a just-opened `'…` literal
// (`WHERE status = '|`, `= 'op|`, `IN ('a', 'op|`) the resolvers previously
// computed the replace range from `getWordUntilPosition` — which starts
// *after* the opening quote — and the emitter inserted a *quoted* literal,
// producing the broken `status = ''open'`. This helper detects the
// in-progress string (via `findValueStringBodyStart`) and returns a range
// that replaces the in-string body together with a `quote:false` flag so the
// emitter inserts a BARE value (the surrounding quotes already exist —
// exactly how `emitJsonPathCandidates` already behaves). Outside a string
// (`= |`, bare partial) it falls back to the word range + a quoted literal.

import { findValueStringBodyStart } from '../context/comparisonScanHelpers';

type WordModel = {
  readonly getWordUntilPosition: (position: {
    lineNumber: number;
    column: number;
  }) => { word: string; startColumn: number; endColumn: number };
};

type Position = { readonly lineNumber: number; readonly column: number };

type MonacoRange = {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
};

export type EnumValueEdit = {
  readonly range: MonacoRange;
  /** The already-typed prefix the ranker should fuzzy-match against. */
  readonly word: string;
  /**
   * `false` ⇒ the cursor is inside an existing `'…` literal: the emitter must
   * insert the bare (still SQL-escaped) value, NOT a re-quoted one.
   */
  readonly quote: boolean;
};

export function computeEnumValueEdit(
  model: WordModel,
  position: Position,
  text: string,
  offset: number,
): EnumValueEdit {
  const bodyStart = findValueStringBodyStart(text, offset);
  if (bodyStart !== undefined) {
    // LIKE-pattern literal whose body so far ENDS in a wildcard — `LIKE '%▮`.
    // Replacing the body would eat the `%` the user just typed, and handing
    // Monaco `%` as the filter word hides every member (nothing fuzzy-matches
    // it). Insert at the caret with an empty filter word instead. Only
    // reachable for a caret that produced NO candidates before
    // `detectComparisonValueContext` learned to accept the wildcard, so no
    // existing placement changes.
    if (/[%_]$/.test(text.slice(bodyStart, offset))) {
      return {
        range: {
          startLineNumber: position.lineNumber,
          startColumn: position.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        },
        word: '',
        quote: false,
      };
    }
    // Strings don't span newlines (v1) and the comparison is single-line, so
    // the body start sits on the cursor's line: convert the offset delta to a
    // column directly off the cursor position.
    // A LIKE pattern may START with wildcards too (`LIKE '%Behind t▮`): they
    // are not part of the value being matched, so the filter word drops them
    // and the replace range starts after them — accepting `Behind the Scenes`
    // yields `'%Behind the Scenes`, and the ranker sees a real prefix instead
    // of a `%` that matches no member (mysql coverage script.16 §3 typed).
    const body = text.slice(bodyStart, offset);
    const leadingWildcards = /^[%_]+/.exec(body)?.[0].length ?? 0;
    const startColumn = position.column - (offset - bodyStart) + leadingWildcards;
    return {
      range: {
        startLineNumber: position.lineNumber,
        startColumn,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      },
      word: body.slice(leadingWildcards),
      quote: false,
    };
  }
  const wordInfo = model.getWordUntilPosition(position);
  return {
    range: {
      startLineNumber: position.lineNumber,
      startColumn: wordInfo.startColumn,
      endLineNumber: position.lineNumber,
      endColumn: wordInfo.endColumn,
    },
    word: String(wordInfo.word ?? ''),
    quote: true,
  };
}
